import type {
  gasEstimateForUnprovenTransfer,
  generateTransferProof,
  populateProvedTransfer
} from '@railgun-community/wallet'
import { loadRailgunSdk } from '@web/utils/railgunSdk'
import {
  calculateGasPrice,
  TXIDVersion,
  type RailgunERC20AmountRecipient,
  type TransactionGasDetails
} from '@railgun-community/shared-models'
import { JsonRpcProvider } from 'ethers'

import { getNetworkNameForChainId, getRpcUrlForChainId } from './railgunCore'
import { derivePublicSigner } from './railgunMnemonic'
import {
  getGasDetailsForTransaction,
  computeGasFeeNativeAndUsd,
  computeGasFeeOptionsNativeAndUsd,
  getErrorMessage,
  isProvingMerkleFailure,
  triggerFullUTXORescanForChain,
  warmMainEngineForChain,
  beginMerkletreeQuiescenceForChain,
  type RailgunGasFeeOption,
  type RailgunGasSpeedName
} from './railgunShared'
import { FEATURES } from '@web/constants/features'
import { sdkInvokeInOffscreen } from '@web/extension-services/background/offscreen/railgunOffscreenClient'

import { RAILGUN_DEBUG } from './railgunCore'

// Engine lives either in-process (flag off) or in the offscreen document (flag on).
// In the offscreen case, calling the SDK directly here would fail because the engine
// state hasn't been initialized in the UI process — route through sdkInvokeInOffscreen.

async function invokeGasEstimateForUnprovenTransfer(
  args: Parameters<typeof gasEstimateForUnprovenTransfer>
): Promise<Awaited<ReturnType<typeof gasEstimateForUnprovenTransfer>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'gasEstimateForUnprovenTransfer', args })
  }
  const { gasEstimateForUnprovenTransfer } = await loadRailgunSdk()
  return gasEstimateForUnprovenTransfer(...args)
}

async function invokeGenerateTransferProof(
  args: Parameters<typeof generateTransferProof>,
  onProgress?: (progress: number) => void
): Promise<void> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    // Functions don't survive structured clone — replace the last positional
    // arg (the SDK's progressCallback) with `null`. The offscreen registry
    // splices the real callback back in from `progressId`.
    const wireArgs = [...args]
    wireArgs[wireArgs.length - 1] = null
    await sdkInvokeInOffscreen({
      method: 'generateTransferProof',
      args: wireArgs,
      onProgress
    })
    return
  }
  const { generateTransferProof } = await loadRailgunSdk()
  await generateTransferProof(...args)
}

async function invokePopulateProvedTransfer(
  args: Parameters<typeof populateProvedTransfer>
): Promise<Awaited<ReturnType<typeof populateProvedTransfer>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'populateProvedTransfer', args })
  }
  const { populateProvedTransfer } = await loadRailgunSdk()
  return populateProvedTransfer(...args)
}

function wrapTransferError(err: unknown): never {
  const message = getErrorMessage('transfer', err)
  throw err instanceof Error && message === err.message ? err : new Error(message, { cause: err })
}

export type PrivateTransferGasEstimation = {
  gasFeeAmountNative: string
  gasFeeUsd?: string
  feeOptions?: RailgunGasFeeOption[]
}

export type PreparedPrivateTransferGasDetails = {
  transactionGasDetails: TransactionGasDetails
  overallBatchMinGasPrice: Awaited<ReturnType<typeof calculateGasPrice>>
}

/**
 * Private transfer uses ERC20-style `tokenAddress` inside the Railgun pool (use wrapped base for native).
 * recipientRailgunAddress must be a valid 0zk... railgun address.
 */
export async function estimatePrivateTransferGas(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  tokenAddress: string
  amount: bigint
  railgunWalletID: string
  encryptionKey: string
  recipientRailgunAddress: string
  memoText?: string
}): Promise<PrivateTransferGasEstimation> {
  const {
    mnemonic,
    chainId,
    tokenAddress,
    amount,
    railgunWalletID,
    encryptionKey,
    recipientRailgunAddress,
    memoText
  } = params

  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun private transfer: ${chainId}`)
  }

  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  const sendWithPublicWallet = true
  const feeTokenDetails = undefined

  const erc20AmountRecipients: RailgunERC20AmountRecipient[] = [
    {
      tokenAddress,
      amount,
      recipientAddress: recipientRailgunAddress
    }
  ]

  await warmMainEngineForChain(chainId, railgunWalletID)

  const originalGasDetails = await getGasDetailsForTransaction(
    networkName,
    0n,
    sendWithPublicWallet,
    wallet,
    chainId
  )

  const { gasEstimate } = await invokeGasEstimateForUnprovenTransfer([
    TXIDVersion.V2_PoseidonMerkle,
    networkName,
    railgunWalletID,
    encryptionKey,
    memoText,
    erc20AmountRecipients,
    [],
    originalGasDetails,
    feeTokenDetails,
    sendWithPublicWallet
  ])

  const transactionGasDetails = await getGasDetailsForTransaction(
    networkName,
    gasEstimate,
    sendWithPublicWallet,
    wallet,
    chainId
  )
  const feeSummary = await computeGasFeeNativeAndUsd({
    gasEstimate,
    gasDetails: transactionGasDetails,
    chainId
  })
  const feeOptions = await computeGasFeeOptionsNativeAndUsd({ gasEstimate, chainId, wallet })
  return { ...feeSummary, feeOptions }
}

export async function generatePrivateTransferProofWithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  tokenAddress: string
  amount: bigint
  railgunWalletID: string
  encryptionKey: string
  recipientRailgunAddress: string
  memoText?: string
  feeSpeed?: RailgunGasSpeedName
}): Promise<PreparedPrivateTransferGasDetails> {
  // Released in the `finally` below — see beginMerkletreeQuiescenceForChain.
  let releaseMerkletree: (() => void) | null = null
  try {
    const {
      mnemonic,
      chainId,
      tokenAddress,
      amount,
      railgunWalletID,
      encryptionKey,
      recipientRailgunAddress,
      memoText,
      feeSpeed
    } = params

    const networkName = getNetworkNameForChainId(chainId)
    if (!networkName) {
      throw new Error(`Unsupported chainId for Railgun private transfer: ${chainId}`)
    }

    const rpcUrl = getRpcUrlForChainId(chainId)
    if (!rpcUrl) {
      throw new Error(`No RPC URL configured for chainId ${chainId}`)
    }

    const provider = new JsonRpcProvider(rpcUrl)
    const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

    const sendWithPublicWallet = true
    const showSenderAddressToRecipient = true
    const broadcasterFeeERC20AmountRecipient: RailgunERC20AmountRecipient | undefined = undefined

    const erc20AmountRecipients: RailgunERC20AmountRecipient[] = [
      {
        tokenAddress,
        amount,
        recipientAddress: recipientRailgunAddress
      }
    ]

    releaseMerkletree = await beginMerkletreeQuiescenceForChain(chainId, railgunWalletID)

    const originalGasDetails = await getGasDetailsForTransaction(
      networkName,
      0n,
      sendWithPublicWallet,
      wallet,
      chainId
    )

    const { gasEstimate } = await invokeGasEstimateForUnprovenTransfer([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      railgunWalletID,
      encryptionKey,
      memoText,
      erc20AmountRecipients,
      [],
      originalGasDetails,
      undefined,
      sendWithPublicWallet
    ])

    const transactionGasDetails = await getGasDetailsForTransaction(
      networkName,
      gasEstimate,
      sendWithPublicWallet,
      wallet,
      chainId,
      feeSpeed
    )

    const overallBatchMinGasPrice = calculateGasPrice(transactionGasDetails)

    const progressCallback = (progress: number) => {
      if (RAILGUN_DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[Railgun private transfer] proof progress', progress)
      }
    }

    await invokeGenerateTransferProof(
      [
        TXIDVersion.V2_PoseidonMerkle,
        networkName,
        railgunWalletID,
        encryptionKey,
        showSenderAddressToRecipient,
        memoText,
        erc20AmountRecipients,
        [],
        broadcasterFeeERC20AmountRecipient,
        sendWithPublicWallet,
        overallBatchMinGasPrice,
        progressCallback
      ],
      progressCallback
    )

    return {
      transactionGasDetails,
      overallBatchMinGasPrice
    }
  } catch (err) {
    // Proof failed against a stale UTXO merkletree — rebuild it in the background
    // so the user's retry can prove (the thrown message tells them to wait+retry).
    if (isProvingMerkleFailure(err)) {
      // Release BEFORE the rescan: it needs the tree unfrozen and the scan worker
      // parked by its own interlock, not by ours.
      releaseMerkletree?.()
      releaseMerkletree = null
      void triggerFullUTXORescanForChain(params.chainId, params.railgunWalletID)
    }
    // `wrapTransferError` is `never`-returning, but TS can't see past the `finally`
    // below, so rethrow explicitly to keep the signature honest.
    throw wrapTransferError(err)
  } finally {
    releaseMerkletree?.()
  }
}

/**
 * Populate-only counterpart to {@link executePreparedPrivateTransferWithSDK}.
 *
 * A1 keystore-isolation milestone 4 (§14 of docs/phase4-railgun-isolation-vi.md):
 * runs entirely inside the nested railgun worker. It builds the populated
 * private-transfer transaction from an already-generated proof + gas details and
 * returns it WITHOUT broadcasting — `populateProvedTransfer` needs neither the
 * mnemonic nor a signer, so no secret is required here (broadcast/signing is
 * deferred to M5). The caller (the worker) normalizes the returned tx to a
 * plain object before posting it across the worker boundary.
 */
export async function populatePrivateTransferTransaction(params: {
  chainId: number
  tokenAddress: string
  amount: bigint
  railgunWalletID: string
  recipientRailgunAddress: string
  gasDetails: PreparedPrivateTransferGasDetails
  memoText?: string
}): Promise<Awaited<ReturnType<typeof populateProvedTransfer>>['transaction']> {
  try {
    const {
      chainId,
      tokenAddress,
      amount,
      railgunWalletID,
      recipientRailgunAddress,
      gasDetails,
      memoText
    } = params

    const networkName = getNetworkNameForChainId(chainId)
    if (!networkName) {
      throw new Error(`Unsupported chainId for Railgun private transfer: ${chainId}`)
    }

    const sendWithPublicWallet = true
    const showSenderAddressToRecipient = true
    const broadcasterFeeERC20AmountRecipient: RailgunERC20AmountRecipient | undefined = undefined

    const erc20AmountRecipients: RailgunERC20AmountRecipient[] = [
      {
        tokenAddress,
        amount,
        recipientAddress: recipientRailgunAddress
      }
    ]

    const { transaction } = await invokePopulateProvedTransfer([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      railgunWalletID,
      showSenderAddressToRecipient,
      memoText,
      erc20AmountRecipients,
      [],
      broadcasterFeeERC20AmountRecipient,
      sendWithPublicWallet,
      gasDetails.overallBatchMinGasPrice,
      gasDetails.transactionGasDetails
    ])

    return transaction
  } catch (err) {
    wrapTransferError(err)
  }
}

export async function executePreparedPrivateTransferWithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  tokenAddress: string
  amount: bigint
  railgunWalletID: string
  recipientRailgunAddress: string
  gasDetails: PreparedPrivateTransferGasDetails
  memoText?: string
}): Promise<string> {
  try {
    const {
      mnemonic,
      chainId,
      tokenAddress,
      amount,
      railgunWalletID,
      recipientRailgunAddress,
      gasDetails,
      memoText
    } = params

    const networkName = getNetworkNameForChainId(chainId)
    if (!networkName) {
      throw new Error(`Unsupported chainId for Railgun private transfer: ${chainId}`)
    }

    const rpcUrl = getRpcUrlForChainId(chainId)
    if (!rpcUrl) {
      throw new Error(`No RPC URL configured for chainId ${chainId}`)
    }

    const provider = new JsonRpcProvider(rpcUrl)
    const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

    const sendWithPublicWallet = true
    const showSenderAddressToRecipient = true
    const broadcasterFeeERC20AmountRecipient: RailgunERC20AmountRecipient | undefined = undefined

    const erc20AmountRecipients: RailgunERC20AmountRecipient[] = [
      {
        tokenAddress,
        amount,
        recipientAddress: recipientRailgunAddress
      }
    ]

    const { transaction } = await invokePopulateProvedTransfer([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      railgunWalletID,
      showSenderAddressToRecipient,
      memoText,
      erc20AmountRecipients,
      [],
      broadcasterFeeERC20AmountRecipient,
      sendWithPublicWallet,
      gasDetails.overallBatchMinGasPrice,
      gasDetails.transactionGasDetails
    ])

    console.log('transaction', transaction)

    const tx = await wallet.sendTransaction(transaction)
    await tx.wait()
    return tx.hash
  } catch (err) {
    console.error('executeRailgunDappPrivateSend failed:', err)
    wrapTransferError(err)
  }
}
